import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/common/Button';
import { School as SchoolIcon } from 'lucide-react';
import { StatusBadge } from '@/components/common/Badge';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { TextField } from '@/components/forms/Field';
import { TimeZoneSelect } from '@/components/forms/TimeZoneSelect';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { useResourceActions } from '@/hooks/useResourceActions';
import { schoolsApi } from '@/services/api/endpoints/schools';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { School } from '@/types/domain';
import { orDash } from '@/utils/format';

const schema = z.object({
  code: z.string().min(1, 'Code is required').max(32),
  name: z.string().min(1, 'Name is required').max(200),
  shortName: z.string().max(64),
  establishedDate: z.string(),
  legalName: z.string().max(200),
  registrationNo: z.string().max(100),
  email: z.string().email('Enter a valid e-mail address').or(z.literal('')),
  phone: z.string().max(32),
  website: z.string().max(255),
  timezone: z.string().max(64),
  locale: z.string().max(16),
});

type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = {
  code: '',
  name: '',
  shortName: '',
  establishedDate: '',
  legalName: '',
  registrationNo: '',
  email: '',
  phone: '',
  website: '',
  timezone: 'UTC',
  locale: 'en',
};

/**
 * Schools are the tenant root, so a school-scoped administrator sees exactly one
 * row here — their own. A platform administrator sees every tenant.
 */
export function SchoolsPage() {
  const { can, refreshProfile } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const table = useTableQuery('name,asc');

  const [editing, setEditing] = useState<School | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<School | null>(null);

  const query = useQuery({
    queryKey: queryKeys.schools.list(table.params),
    queryFn: () => schoolsApi.list(table.params),
  });

  const { statusMutation, deleteMutation, invalidateAll } = useResourceActions({
    invalidates: [queryKeys.schools.all, queryKeys.campuses.all],
    changeStatus: schoolsApi.changeStatus,
    remove: schoolsApi.remove,
    label: 'School',
  });

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const blank = (value: string) => (value === '' ? undefined : value);
      const common = {
        name: values.name,
        shortName: blank(values.shortName),
        legalName: blank(values.legalName),
        registrationNo: blank(values.registrationNo),
        email: blank(values.email),
        phone: blank(values.phone),
        website: blank(values.website),
        establishedDate: blank(values.establishedDate),
        timezone: values.timezone,
        locale: values.locale,
      };
      // The code is only accepted on create: it is the stable identifier other
      // systems and printed records refer to, so the API refuses to change it.
      return editing === null
        ? schoolsApi.create({ ...common, code: values.code })
        : schoolsApi.update(editing.id, common);
    },
    onSuccess: () => {
      toast.success(editing === null ? 'School created' : 'School updated');
      close();
      invalidateAll();
      // The dashboard clock reads the school's zone from the signed-in profile.
      void refreshProfile();
    },
    onError: (error) => handleApiError(error, form.setError),
  });

  function openCreate() {
    setEditing(null);
    form.reset(EMPTY);
    setOpen(true);
  }

  function openEdit(school: School) {
    setEditing(school);
    form.reset({
      code: school.code,
      name: school.name,
      shortName: school.shortName ?? '',
      establishedDate: school.establishedDate ?? '',
      legalName: school.legalName ?? '',
      registrationNo: school.registrationNo ?? '',
      email: school.email ?? '',
      phone: school.phone ?? '',
      website: school.website ?? '',
      timezone: school.timezone,
      locale: school.locale,
    });
    setOpen(true);
  }

  function close() {
    setOpen(false);
    setEditing(null);
  }

  const columns: Column<School>[] = [
    {
      key: 'code',
      header: 'Code',
      sortKey: 'code',
      render: (school) => <span className="font-medium text-ink">{school.code}</span>,
    },
    { key: 'name', header: 'Name', sortKey: 'name', render: (school) => school.name },
    { key: 'email', header: 'E-mail', render: (school) => orDash(school.email) },
    { key: 'timezone', header: 'Time zone', render: (school) => school.timezone },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (school) => <StatusBadge status={school.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (school) => (
        <div className="flex justify-end gap-1">
          {can(PERMISSIONS.SCHOOL_UPDATE) && (
            <>
              <Button size="sm" variant="ghost" onClick={() => openEdit(school)}>
                Edit
              </Button>
              <Button
                size="sm"
                variant="ghost"
                loading={statusMutation.isPending}
                onClick={() =>
                  statusMutation.mutate({
                    id: school.id,
                    payload: { status: school.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' },
                  })
                }
              >
                {school.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
              </Button>
            </>
          )}
          {can(PERMISSIONS.SCHOOL_DELETE) && (
            <Button size="sm" variant="ghost" onClick={() => setDeleting(school)}>
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <ListPage
        title="Schools"
        description="The tenant root. All campuses, academic structure and students belong to a school."
        icon={<SchoolIcon className="h-5 w-5" aria-hidden="true" />}
        action={can(PERMISSIONS.SCHOOL_CREATE) ? <Button onClick={openCreate}>New school</Button> : undefined}
        search={{ value: table.searchInput, onChange: table.setSearchInput, placeholder: 'Search schools…' }}
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(school) => school.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="No schools found"
        />
      </ListPage>

      <Modal
        open={open}
        size="lg"
        title={editing === null ? 'New school' : `Edit ${editing.name}`}
        onClose={close}
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button onClick={form.handleSubmit((values) => save.mutate(values))} loading={save.isPending}>
              Save
            </Button>
          </>
        }
      >
        <form className="space-y-4" noValidate onSubmit={form.handleSubmit((values) => save.mutate(values))}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Code"
              required
              disabled={editing !== null}
              hint={editing !== null ? 'Fixed after creation' : 'Unique across the deployment'}
              error={form.formState.errors.code?.message}
              {...form.register('code')}
            />
            <TextField label="Name" required error={form.formState.errors.name?.message} {...form.register('name')} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Short name"
              hint="For navigation and reports"
              error={form.formState.errors.shortName?.message}
              {...form.register('shortName')}
            />
            <TextField
              label="Established"
              type="date"
              error={form.formState.errors.establishedDate?.message}
              {...form.register('establishedDate')}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="Legal name" error={form.formState.errors.legalName?.message} {...form.register('legalName')} />
            <TextField
              label="Registration number"
              error={form.formState.errors.registrationNo?.message}
              {...form.register('registrationNo')}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="E-mail" type="email" error={form.formState.errors.email?.message} {...form.register('email')} />
            <TextField label="Phone" error={form.formState.errors.phone?.message} {...form.register('phone')} />
          </div>
          <TextField
            label="Website"
            hint="Include http:// or https://"
            error={form.formState.errors.website?.message}
            {...form.register('website')}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <TimeZoneSelect
              label="Time zone"
              hint="Dates, reports and every dashboard clock use this zone"
              error={form.formState.errors.timezone?.message}
              currentValue={form.watch('timezone')}
              {...form.register('timezone')}
            />
            <TextField label="Locale" error={form.formState.errors.locale?.message} {...form.register('locale')} />
          </div>
        </form>
      </Modal>
      <ConfirmDialog
        open={deleting !== null}
        title="Delete school"
        message={
          deleting === null
            ? ''
            : `Delete ${deleting.name}? This is refused while any campus, academic year, grade, subject or student belongs to it — deactivate it instead.`
        }
        confirmLabel="Delete"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleting !== null) {
            deleteMutation.mutate(deleting.id, { onSettled: () => setDeleting(null) });
          }
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}
